import type { ApiResponse } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import { bangkokWallTimeToRfc3339 } from '../commission-ledger/types';
import type { WalletRepository } from './repository';
import type { WalletEntryQuery, WalletListQuery } from './types';

type WalletListResponse = ApiResponse<'/api/v1/affiliate-wallets', 'get'>;
type WalletDetailResponse = ApiResponse<'/api/v1/affiliate-wallets/{id}', 'get'>;
type WalletEntriesResponse = ApiResponse<'/api/v1/affiliate-wallets/{id}/entries', 'get'>;
type WalletAdjustmentPreviewResponse = ApiResponse<
  '/api/v1/affiliate-wallets/{id}/adjustments/preview',
  'post'
>;
type WalletAdjustmentResponse = ApiResponse<'/api/v1/affiliate-wallets/{id}/adjustments', 'post'>;

export function createRealWalletRepository(
  client: ApiClient,
  accessToken: string | null,
): WalletRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async list(query, signal) {
      const response = await client.get<WalletListResponse>('/api/v1/affiliate-wallets', {
        ...auth,
        query: walletListQuery(query),
        signal,
      });
      return {
        items: response.data.data,
        nextCursor: response.data.pagination.nextCursor,
        requestId: response.requestId,
      };
    },
    async get(walletId, signal) {
      const response = await client.get<WalletDetailResponse>(walletPath(walletId), {
        ...auth,
        signal,
      });
      return response.data.data;
    },
    async listEntries(walletId, query, signal) {
      const response = await client.get<WalletEntriesResponse>(`${walletPath(walletId)}/entries`, {
        ...auth,
        query: walletEntryQuery(query),
        signal,
      });
      return {
        items: response.data.data,
        nextCursor: response.data.pagination.nextCursor,
        requestId: response.requestId,
      };
    },
    async changeStatus(walletId, input) {
      // The status OpenAPI operation intentionally has no Idempotency-Key parameter.
      const response = await client.post<WalletDetailResponse>(
        `${walletPath(walletId)}/status`,
        input,
        auth,
      );
      return { data: response.data.data, requestId: response.requestId };
    },
    async previewAdjustment(walletId, input) {
      const response = await client.post<WalletAdjustmentPreviewResponse>(
        `${walletPath(walletId)}/adjustments/preview`,
        input,
        auth,
      );
      return response.data.data;
    },
    async createAdjustment(walletId, input, idempotencyKey) {
      const response = await client.post<WalletAdjustmentResponse>(
        `${walletPath(walletId)}/adjustments`,
        input,
        { ...auth, idempotencyKey: requireUuid(idempotencyKey) },
      );
      return { data: response.data.data, requestId: response.requestId };
    },
  };
}

function walletListQuery(query: WalletListQuery): Readonly<Record<string, unknown>> {
  const filters = query.filters;
  return compact({
    cursor: query.cursor,
    limit: query.pageSize,
    sort: query.sort,
    partnerId: filters.partnerId.trim(),
    agentId: filters.agentId.trim(),
    memberId: filters.memberId.trim(),
    username: filters.username.trim(),
    partnerUserId: filters.partnerUserId.trim(),
    currency: filters.currency.trim().toUpperCase(),
    status: filters.status,
    reconciliationStatus: filters.reconciliationStatus,
    minimumAvailableBalance: filters.minimumAvailableBalance.trim(),
    maximumAvailableBalance: filters.maximumAvailableBalance.trim(),
    minimumReservedBalance: filters.minimumReservedBalance.trim(),
    maximumReservedBalance: filters.maximumReservedBalance.trim(),
  });
}

function walletEntryQuery(query: WalletEntryQuery): Readonly<Record<string, unknown>> {
  const filters = query.filters;
  return compact({
    cursor: query.cursor,
    limit: query.pageSize,
    sort: query.sort,
    entryType: filters.entryType,
    sourceTransactionId: filters.sourceTransactionId.trim(),
    sourceWithdrawalId: filters.sourceWithdrawalId.trim(),
    sourceAdjustmentId: filters.sourceAdjustmentId.trim(),
    minimumAmount: filters.minimumAmount.trim(),
    maximumAmount: filters.maximumAmount.trim(),
    effectiveFrom: bangkokWallTimeToRfc3339(filters.effectiveFrom),
    effectiveTo: bangkokWallTimeToRfc3339(filters.effectiveTo),
  });
}

function compact(values: Readonly<Record<string, unknown>>) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ''));
}

function walletPath(walletId: string): string {
  return `/api/v1/affiliate-wallets/${encodeURIComponent(walletId)}`;
}

function requireUuid(value: string): string {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value)) {
    throw new Error('A UUID v4 idempotency key is required');
  }
  return value;
}
